import { MaybeRef, computed, toValue } from 'vue'

import useFixedNumber from '@/composables/useFixedNumber'

import { ExtendedRoom } from '@/modules/hotels/interfaces/extendedRoom'
import { Room } from '@/modules/hotels/interfaces/hotelDetailsResponse'

const OrderConditions = {
	AscendingSort: 'ascending',
	DescendingSort: 'descending',
	LowerPriceAgreement: 'lowerPriceAgreement',
	Agreement: 'agreement',
	AgreementCompany: 'agreementCompany',
} as const

export interface UseRoomListFormattingProps {
	rooms: MaybeRef<Room[]>
	orderBy: MaybeRef<string>
	orderByBeds: MaybeRef<string>
}

interface SortContext {
	a: ExtendedRoom
	b: ExtendedRoom
	hasAgreementA: boolean
	hasAgreementB: boolean
	customerTotalAscending: number
	customerTotalDescending: number
}

const compareByLowestRate = (a: ExtendedRoom, b: ExtendedRoom): number => {
	if (a.isLowestRate && !b.isLowestRate) return -1
	if (!a.isLowestRate && b.isLowestRate) return 1
	return 0
}

const compareByAgreement = (a: ExtendedRoom, b: ExtendedRoom): number => {
	if (a.agreement && !b.agreement) return -1
	if (!a.agreement && b.agreement) return 1
	if (a.companyAgreement && !b.companyAgreement) return -1
	if (!a.companyAgreement && b.companyAgreement) return 1
	return 0
}

const compareByHasAgreement = (hasA: boolean, hasB: boolean): number => {
	if (hasA && !hasB) return -1
	if (!hasA && hasB) return 1
	return 0
}

const sortStrategies: Record<string, (ctx: SortContext) => number> = {
	[OrderConditions.AscendingSort]: (ctx) => {
		if (ctx.customerTotalAscending !== 0) return ctx.customerTotalAscending
		return compareByAgreement(ctx.a, ctx.b)
	},

	[OrderConditions.DescendingSort]: (ctx) => {
		if (ctx.customerTotalDescending !== 0) return ctx.customerTotalDescending

		const lowestRateOrder = compareByLowestRate(ctx.a, ctx.b)
		if (lowestRateOrder !== 0) return lowestRateOrder

		return compareByAgreement(ctx.a, ctx.b)
	},

	[OrderConditions.LowerPriceAgreement]: (ctx) => {
		const lowestRateOrder = compareByLowestRate(ctx.a, ctx.b)
		if (lowestRateOrder !== 0) return lowestRateOrder

		const agreementOrder = compareByHasAgreement(
			ctx.hasAgreementA,
			ctx.hasAgreementB
		)
		if (agreementOrder !== 0) return agreementOrder

		return ctx.customerTotalAscending
	},

	[OrderConditions.Agreement]: (ctx) => {
		const lowestRateOrder = compareByLowestRate(ctx.a, ctx.b)
		if (lowestRateOrder !== 0) return lowestRateOrder

		return compareByAgreement(ctx.a, ctx.b)
	},

	[OrderConditions.AgreementCompany]: (ctx) => {
		const lowestRateOrder = compareByLowestRate(ctx.a, ctx.b)
		if (lowestRateOrder !== 0) return lowestRateOrder

		if (ctx.a.companyAgreement && !ctx.b.companyAgreement) return -1
		if (!ctx.a.companyAgreement && ctx.b.companyAgreement) return 1
		return 0
	},
}

const getBedTypeCode = (roomType: string | undefined): string => {
	if (roomType && roomType.length >= 3) {
		return roomType.slice(2, 3)
	}
	return ''
}

const useRoomListFormatting = (props: UseRoomListFormattingProps) => {
	const roomsList = computed(() => {
		let result = [...toValue(props.rooms)] as ExtendedRoom[]
		const orderByValue = toValue(props.orderBy)
		const orderByBedsValue = toValue(props.orderByBeds)

		result = result.map((room) => {
			const uniqueAmenities = [...new Set(room.amenities)]

			return {
				...room,
				amenities: uniqueAmenities,
				rawTotal: useFixedNumber(room.fare).rawNumber,
				exceedOfAmenities: uniqueAmenities.length > 3,
				someAmenities: uniqueAmenities.slice(0, 3),
				remainAmenitiesAmount:
					uniqueAmenities.length - uniqueAmenities.slice(0, 3).length,
			}
		})

		result = result.sort((a, b) => {
			if (a.errors?.length && !b.errors?.length) return 1
			if (!a.errors?.length && b.errors?.length) return -1

			const priceA = useFixedNumber(a.fareApproximate).rawNumber
			const priceB = useFixedNumber(b.fareApproximate).rawNumber

			const ctx: SortContext = {
				a,
				b,
				hasAgreementA: a.agreement || a.companyAgreement,
				hasAgreementB: b.agreement || b.companyAgreement,
				customerTotalAscending: priceA - priceB,
				customerTotalDescending: priceB - priceA,
			}

			const sortFn = sortStrategies[orderByValue]
			return sortFn ? sortFn(ctx) : 0
		})

		if (orderByBedsValue && orderByBedsValue !== 'all') {
			result = result.filter((room) => {
				if (!room.roomType || room.roomType.length < 3) return false

				if (orderByBedsValue === 'double') {
					const bedAmount = parseInt(room.roomType.slice(1, 2))
					return bedAmount >= 2
				}

				const bedTypeCode = getBedTypeCode(room.roomType)
				return bedTypeCode === orderByBedsValue
			})
		}

		return result
	})

	return {
		roomsList,
	}
}

export default useRoomListFormatting
